import type { MenuAccess } from '../menuConfig';
import type { PermissionKey } from '../permissions';
import type {
  AccessChanges,
  Catalog,
  ElementState,
  RoleView,
  UiElement,
} from './roleCenterApi';

/**
 * Reguły ekranu centrum ról (decyzja użytkownika 22.10.2026, przełącznik
 * `roleCenter`, S-22.10-01) — czyste funkcje bez Reacta. Rozstrzyga API
 * (`role-center/role-access.ts`); ekran liczy to samo dla zmian jeszcze
 * niezapisanych: co rola będzie mieć po zapisie, co będzie wyszarzone
 * i jaka lista zmian trafi do okna „było → jest”.
 */

/** Uprawnienia roli po zmianach w toku. */
export function grantedWith(role: RoleView, changes: AccessChanges): string[] {
  const set = new Set(role.permissions);
  for (const [key, on] of Object.entries(changes.permissions ?? {})) {
    if (on) set.add(key);
    else set.delete(key);
  }
  return role.permissionCells.map((c) => c.key).filter((k) => set.has(k));
}

/** Wyjaśnienie wyszarzenia — to samo zdanie co w API. */
export function missingPermissionReason(element: UiElement, catalog: Catalog): string {
  const names = element.anyOf.map(
    (k) => `„${catalog.permissions.find((p) => p.key === k)?.name ?? k}”`,
  );
  return names.length > 1
    ? `Rola nie ma żadnego z uprawnień ${names.join(' ani ')} — elementu nie da się włączyć. Ukrycie w interfejsie niczego nie odbiera: o dostępie i tak rozstrzyga uprawnienie.`
    : `Rola nie ma uprawnienia ${names[0]} — elementu nie da się włączyć. Ukrycie w interfejsie niczego nie odbiera: o dostępie i tak rozstrzyga uprawnienie.`;
}

/** Stan elementów po zmianach w toku (wyszarzenie liczone z uprawnień po zmianie). */
export function elementStatesWith(
  role: RoleView,
  changes: AccessChanges,
  catalog: Catalog,
): ElementState[] {
  if (role.locked) return role.elements;
  const granted = grantedWith(role, changes);
  return catalog.elements.map((element) => {
    const saved = role.elements.find((e) => e.key === element.key);
    const pending = changes.visibility?.[element.key];
    const hiddenExplicitly =
      pending === undefined ? (saved?.hiddenExplicitly ?? false) : !pending;
    const permitted =
      element.anyOf.length === 0 || element.anyOf.some((k) => granted.includes(k));
    return {
      key: element.key,
      visible: permitted && !hiddenExplicitly,
      permitted,
      hiddenExplicitly,
      locked: !permitted,
      lockReason: permitted ? null : missingPermissionReason(element, catalog),
    };
  });
}

export type ChangeLine = {
  kind: 'permission' | 'visibility';
  key: string;
  name: string;
  from: string;
  to: string;
};

/** Lista zmian do okna zapisu — tylko te, które coś zmieniają. */
export function changeLines(
  role: RoleView,
  changes: AccessChanges,
  catalog: Catalog,
): ChangeLine[] {
  const lines: ChangeLine[] = [];
  for (const p of catalog.permissions) {
    const to = changes.permissions?.[p.key];
    if (to === undefined) continue;
    const from = role.permissions.includes(p.key);
    if (from === to) continue;
    lines.push({
      kind: 'permission',
      key: p.key,
      name: p.name,
      from: from ? 'nadane' : 'brak',
      to: to ? 'nadane' : 'brak',
    });
  }
  for (const e of catalog.elements) {
    const to = changes.visibility?.[e.key];
    if (to === undefined) continue;
    const from = !(role.elements.find((s) => s.key === e.key)?.hiddenExplicitly ?? false);
    if (from === to) continue;
    lines.push({
      kind: 'visibility',
      key: e.key,
      name: e.name,
      from: from ? 'widoczny' : 'ukryty',
      to: to ? 'widoczny' : 'ukryty',
    });
  }
  return lines;
}

/**
 * Zmiana przełącznika w toku: powrót do stanu zapisanego zdejmuje wpis,
 * żeby lista zmian pokazywała tylko to, co naprawdę się zmienia.
 */
export function toggleChange(
  changes: AccessChanges,
  kind: 'permissions' | 'visibility',
  key: string,
  value: boolean,
  saved: boolean,
): AccessChanges {
  const next = { ...(changes[kind] ?? {}) };
  if (value === saved) delete next[key];
  else next[key] = value;
  return { ...changes, [kind]: next };
}

/** Liczba zmian w toku. */
export const pendingCount = (changes: AccessChanges) =>
  Object.keys(changes.permissions ?? {}).length + Object.keys(changes.visibility ?? {}).length;

/**
 * Podgląd „Zobacz jako”: menu i sekcje oczami wybranej roli — z jej danych
 * w centrum, bez przelogowania. Administrator widzi wszystko.
 */
export function previewAccess(role: RoleView): MenuAccess {
  const admin = role.id === 'ADMIN';
  return {
    has: (p: PermissionKey) => admin || role.permissions.includes(p),
    hidden: (key: string) =>
      !admin && (role.elements.find((e) => e.key === key)?.hiddenExplicitly ?? false),
    fromCenter: true,
  };
}

/** Elementy spoza menu, które rola widzi, i te, których nie widzi. */
export function sectionsFor(
  role: RoleView,
  catalog: Catalog,
): { visible: UiElement[]; hidden: UiElement[] } {
  const visible: UiElement[] = [];
  const hidden: UiElement[] = [];
  for (const e of catalog.elements) {
    if (e.kind === 'menu') continue;
    const state = role.elements.find((s) => s.key === e.key);
    (role.id === 'ADMIN' || state?.visible ? visible : hidden).push(e);
  }
  return { visible, hidden };
}
